import { Button, Card, Col, Divider, Row, Space } from "antd";
import { useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import DriverClassificationContext from "../../../context/driver-classification/context";
import DriverClassificationContextProvider from "../../../context/driver-classification/provider";
import { IUpdateDriverClassification } from "../../../models/driver-classification/request";
import EndPoints from "../../../services/end-points";
import { execute } from "../../../utils/helpers/execute";
import FieldBuilder from "../../form-components/field-builder";
import Spin from "../../general/antd/spin";
import styles from "./style.module.scss";

const Form = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { details, actions, loading } = useContext(
        DriverClassificationContext
    );
    const { id } = useParams();

    const methods = useForm<IUpdateDriverClassification>({
        defaultValues: {
            name: details?.name || "",
            description: details?.description || "",
            calculatingDeliveryCostPerKilometer:
                details?.calculatingDeliveryCostPerKilometer || false,
            deliveryCostPerKilometer: details?.deliveryCostPerKilometer
                ? parseFloat(details.deliveryCostPerKilometer)
                : undefined,
            orderDeliveryCost: details?.orderDeliveryCost
                ? parseFloat(details.orderDeliveryCost)
                : undefined,
            extraOrderFixedBonusEnabled:
                details?.extraOrderFixedBonusEnabled || false,
            extraOrderFixedBonusAmount: details?.extraOrderFixedBonusAmount
                ? parseFloat(details.extraOrderFixedBonusAmount)
                : undefined,
        },
    });

    const {
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = methods;

    const calculatingPerKm = watch("calculatingDeliveryCostPerKilometer");
    const extraBonusEnabled = watch("extraOrderFixedBonusEnabled");

    useEffect(() => {
        id && actions.getDetails(Number(id));
    }, [id]);

    const [loadingSubmit, setLoadingSubmit] = useState(false);

    const onSubmit = async (data: IUpdateDriverClassification) => {
        await execute({
            callback: async () => {
                setLoadingSubmit(true);
                if (details) {
                    await EndPoints.driverClassification.updateDriverClassification(
                        details.id,
                        {
                            ...data,
                            description: data.description || null,
                        }
                    );
                } else {
                    await EndPoints.driverClassification.createDriverClassification({
                        ...data,
                        description: data.description || null,
                    });
                }
                navigate(-1);
            },
            fallback: (error) => {
            },
            finallyCallback: () => {
                setLoadingSubmit(false);
            },
            throwException: false,
        });
    };

    useEffect(() => {
        reset({
            name: details?.name || "",
            description: details?.description || "",
            calculatingDeliveryCostPerKilometer:
                details?.calculatingDeliveryCostPerKilometer || false,
            deliveryCostPerKilometer: details?.deliveryCostPerKilometer
                ? parseFloat(details.deliveryCostPerKilometer)
                : undefined,
            orderDeliveryCost: details?.orderDeliveryCost
                ? parseFloat(details.orderDeliveryCost)
                : undefined,
            extraOrderFixedBonusEnabled:
                details?.extraOrderFixedBonusEnabled || false,
            extraOrderFixedBonusAmount: details?.extraOrderFixedBonusAmount
                ? parseFloat(details.extraOrderFixedBonusAmount)
                : undefined,
        });
    }, [details]);

    return (
        <FormProvider {...methods}>
            <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
                <div>
                    <span className={styles.title}>
                        {details
                            ? t("update_driver_classification")
                            : t("add_driver_classification")}
                    </span>
                    <Divider className={styles.divider} />
                    {loading.includes("details") && id ? (
                        <div style={{ height: "60vh" }}>
                            <Spin />
                        </div>
                    ) : (
                        <div className={styles.children}>
                            <Space direction="vertical" size="large" style={{ width: "100%" }}>
                                {/* Classification Information Section */}
                                <Card title={t("classification_information")} bordered={false}>
                                    <Row gutter={[16, 16]}>
                                        <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
                                            <FieldBuilder
                                                name="name"
                                                label={t("classification_name")}
                                                input={{ type: "text" }}
                                                rules={{ required: true }}
                                            />
                                        </Col>

                                        <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
                                            <FieldBuilder
                                                name="description"
                                                label={t("description")}
                                                input={{ type: "text-area", rows: 4 }}
                                                rules={{ required: false }}
                                            />
                                        </Col>
                                    </Row>
                                </Card>

                                {/* Delivery Cost Settings Section */}
                                <Card title={t("delivery_cost_settings")} bordered={false}>
                                    <Row gutter={[16, 16]}>
                                        <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
                                            <Space direction="vertical" size="small" style={{ width: "100%" }}>
                                                <FieldBuilder
                                                    name="calculatingDeliveryCostPerKilometer"
                                                    label={t("calculate_cost_per_km")}
                                                    input={{
                                                        type: "switch",
                                                    }}
                                                    rules={{ required: false }}
                                                />
                                                <small style={{ color: "#888", marginTop: 4 }}>
                                                    {t("calculate_cost_per_km_help")}
                                                </small>
                                            </Space>
                                        </Col>

                                        {calculatingPerKm ? (
                                            <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
                                                <FieldBuilder
                                                    name="deliveryCostPerKilometer"
                                                    label={t("delivery_cost_per_km")}
                                                    input={{
                                                        type: "number",
                                                    }}
                                                    suffix={t("currency")}
                                                    rules={{
                                                        required: calculatingPerKm,
                                                        min: 0,
                                                    }}
                                                />
                                            </Col>
                                        ) : (
                                            <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
                                                <FieldBuilder
                                                    name="orderDeliveryCost"
                                                    label={t("fixed_order_delivery_cost")}
                                                    input={{
                                                        type: "number",
                                                    }}
                                                    suffix={t("currency")}
                                                    rules={{
                                                        required: !calculatingPerKm,
                                                        min: 0,
                                                    }}
                                                />
                                            </Col>
                                        )}
                                    </Row>
                                </Card>

                                {/* Extra Order Bonus Settings Section */}
                                {(
                                    <Card title={t("extra_order_bonus_settings")} bordered={false}>
                                        <Row gutter={[16, 16]}>
                                            <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
                                                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                                                    <FieldBuilder
                                                        name="extraOrderFixedBonusEnabled"
                                                        label={t("extra_order_fixed_bonus_enabled")}
                                                        input={{
                                                            type: "switch",
                                                        }}
                                                        rules={{ required: false }}
                                                    />
                                                    <small style={{ color: "#888", marginTop: 4 }}>
                                                        {t("extra_order_bonus_help")}
                                                    </small>
                                                </Space>
                                            </Col>

                                            {extraBonusEnabled && (
                                                <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
                                                    <FieldBuilder
                                                        name="extraOrderFixedBonusAmount"
                                                        label={t("extra_order_fixed_bonus_amount")}
                                                        input={{
                                                            type: "number",
                                                        }}
                                                        suffix={t("currency")}
                                                        rules={{
                                                            required: extraBonusEnabled,
                                                            min: 0,
                                                        }}
                                                    />
                                                </Col>
                                            )}
                                        </Row>
                                    </Card>
                                )}
                            </Space>
                        </div>
                    )}
                </div>

                <div>
                    <Divider className={styles.divider} />
                    <div className={styles.formFooter}>
                        <Button
                            onClick={() => {
                                navigate(-1);
                            }}
                        >
                            {t("cancel")}
                        </Button>
                        <Button loading={loadingSubmit} htmlType="submit" type="primary">
                            {t("save")}
                        </Button>
                    </div>
                </div>
            </form>
        </FormProvider>
    );
};

const DriverClassificationForm = () => {
    return (
        <DriverClassificationContextProvider>
            <Form />
        </DriverClassificationContextProvider>
    );
};

export default DriverClassificationForm;
